<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useRoute } from "vue-router";
  import moment from "moment";
  import { storeToRefs } from "pinia";
  import TwoPanePage from "@templates/TwoPanePage.vue";
  import { useAdTemplatesStore } from "@stores/adTemplates";
  import { useToastsStore } from "@stores/toastsStore";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Card, CardContent } from "@/components/ui/card";
  import { Input } from "@/components/ui/input";
  import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { CodeEditor } from "@/components/episto-ui/code-editor";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import AdTemplatePreviewPanel from "@/components/MCampaign/AdTemplates/AdTemplatePreviewPanel.vue";
  import AdTemplateVariablesEditor from "@/components/MCampaign/AdTemplates/AdTemplateVariablesEditor.vue";
  import AdTemplateFontsEditor from "@/components/MCampaign/AdTemplates/AdTemplateFontsEditor.vue";
  import AdTemplateHistory from "@/components/MCampaign/AdTemplates/AdTemplateHistory.vue";
  import { serverErrorDetail } from "@utils/serverErrorDetail";
  import type { AdTemplateVersion } from "@/types";

  const route = useRoute();
  const toast = useToastsStore();
  const adTemplatesStore = useAdTemplatesStore();
  const {
    template,
    working,
    baseVersion,
    workingDraft,
    versions,
    errors,
    fonts,
    backgrounds,
    hasUnsavedChanges,
    canSave,
  } = storeToRefs(adTemplatesStore);

  const saving = ref(false);
  const versionToPublish = ref<AdTemplateVersion | null>(null);
  const isPublishDialogOpen = ref(false);

  const token = computed(() => String(route.params.token));
  const documentUrl = computed(() => workingDraft.value?.document_url ?? baseVersion.value?.document_url ?? "");
  const openedVersionLabel = computed(() => {
    if (!baseVersion.value) return "";
    if (working.value?.versionId) return `Draft v${baseVersion.value.number}`;
    return `${baseVersion.value.status === "published" ? "Published" : "Archived"} v${baseVersion.value.number}`;
  });
  const canPublishOpened = computed(() => !!workingDraft.value && !hasUnsavedChanges.value);

  const load = async () => {
    try {
      await adTemplatesStore.fetchTemplate(token.value);
    } catch (error) {
      toast.error("Failed to load the template", serverErrorDetail(error));
    }
  };

  const saveDraft = async () => {
    saving.value = true;
    try {
      const saved = await adTemplatesStore.saveDraft();
      if (saved) toast.success(`Draft v${saved.number} saved`);
      else toast.error("The template does not follow the contract", errors.value.join(" · "));
    } catch (error) {
      toast.error("Failed to save the draft", serverErrorDetail(error));
    } finally {
      saving.value = false;
    }
  };

  const askPublish = (version: AdTemplateVersion) => {
    versionToPublish.value = version;
    isPublishDialogOpen.value = true;
  };

  const confirmPublish = async () => {
    if (!versionToPublish.value) return;
    try {
      await adTemplatesStore.publish(versionToPublish.value);
      toast.success(`v${versionToPublish.value.number} published`);
    } catch (error) {
      toast.error("Failed to publish", serverErrorDetail(error));
    }
  };

  const startHtmlVersion = async () => {
    try {
      await adTemplatesStore.startHtmlVersion();
    } catch (error) {
      toast.error("Failed to start the HTML version", serverErrorDetail(error));
    }
  };

  const archiving = ref(false);
  const isArchiveDialogOpen = ref(false);
  const canArchive = computed(() => !!template.value?.archivable && !template.value.archived_at);

  const setArchived = async (archive: boolean) => {
    archiving.value = true;
    try {
      await adTemplatesStore.setArchived(archive);
      toast.success(archive ? "Template archived" : "Template unarchived");
    } catch (error) {
      toast.error(archive ? "Failed to archive the template" : "Failed to unarchive the template", serverErrorDetail(error));
    } finally {
      archiving.value = false;
    }
  };

  const refreshDocumentUrl = async () => {
    const versionId = workingDraft.value?.id ?? baseVersion.value?.id;
    if (versionId) await adTemplatesStore.refreshDocumentUrl(versionId);
  };

  onMounted(() => {
    load();
    adTemplatesStore.fetchFonts();
    adTemplatesStore.fetchBackgrounds();
  });

  watch(token, load);
</script>

<template>
  <div
    v-if="template"
    class="flex flex-col gap-4">
    <div
      v-if="template.archived_at"
      class="flex items-center justify-between gap-3 rounded-md border bg-muted p-3 text-sm">
      <p>
        Archived on {{ moment(template.archived_at).format("D MMM YYYY") }}: new creatives can no longer pick this
        template, the creatives already using it keep it.
      </p>
      <Button
        size="sm"
        variant="outline"
        :disabled="archiving"
        @click="setArchived(false)">
        Unarchive
      </Button>
    </div>

    <template v-if="working">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <Badge :variant="template.renderer === 'html' ? 'info' : 'secondary'">
            {{ template.renderer === "html" ? "HTML" : "Bannerbear" }}
          </Badge>
          <Badge variant="secondary">{{ openedVersionLabel }}</Badge>
          <span
            v-if="hasUnsavedChanges"
            class="text-xs text-status-warning-strong">
            Unsaved changes
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="canArchive"
            variant="ghost"
            :disabled="archiving"
            @click="isArchiveDialogOpen = true">
            Archive
          </Button>
          <Input
            v-model="working.message"
            class="w-72 text-sm"
            placeholder="What does this version change?" />
          <Button
            variant="outline"
            :disabled="!canSave || saving"
            @click="saveDraft">
            Save draft
          </Button>
          <Button
            :disabled="!canPublishOpened"
            @click="workingDraft && askPublish(workingDraft)">
            Publish
          </Button>
        </div>
      </div>

      <div
        v-if="errors.length"
        class="rounded-md border border-status-error-border bg-status-error-soft p-3 text-xs text-status-error-strong">
        <p
          v-for="message in errors"
          :key="message">
          {{ message }}
        </p>
      </div>

      <TwoPanePage
        :span-left="7"
        :span-right="5">
        <template #left>
          <Card>
            <CardContent class="p-4">
              <Tabs default-value="feed-html">
                <TabsList>
                  <TabsTrigger value="feed-html">Feed HTML</TabsTrigger>
                  <TabsTrigger value="feed-css">Feed CSS</TabsTrigger>
                  <TabsTrigger value="story-html">Story HTML</TabsTrigger>
                  <TabsTrigger value="story-css">Story CSS</TabsTrigger>
                  <TabsTrigger value="variables">Variables</TabsTrigger>
                  <TabsTrigger value="fonts">Fonts</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>
                <TabsContent
                  value="feed-html"
                  class="h-[60vh] flex-none">
                  <CodeEditor
                    v-model="working.html_templates.feed.html"
                    language="html" />
                </TabsContent>
                <TabsContent
                  value="feed-css"
                  class="h-[60vh] flex-none">
                  <CodeEditor
                    v-model="working.html_templates.feed.css"
                    language="css" />
                </TabsContent>
                <TabsContent
                  value="story-html"
                  class="h-[60vh] flex-none">
                  <CodeEditor
                    v-model="working.html_templates.story.html"
                    language="html" />
                </TabsContent>
                <TabsContent
                  value="story-css"
                  class="h-[60vh] flex-none">
                  <CodeEditor
                    v-model="working.html_templates.story.css"
                    language="css" />
                </TabsContent>
                <TabsContent value="variables">
                  <AdTemplateVariablesEditor v-model="working.m_template_attributes" />
                </TabsContent>
                <TabsContent value="fonts">
                  <AdTemplateFontsEditor
                    v-model="working.html_templates.fonts"
                    :available="fonts" />
                </TabsContent>
                <TabsContent value="history">
                  <AdTemplateHistory
                    :versions="versions"
                    :opened-version-id="baseVersion?.id ?? null"
                    :locked="hasUnsavedChanges"
                    @open="adTemplatesStore.restore"
                    @publish="askPublish" />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </template>
        <template #right>
          <Card class="sticky top-4">
            <CardContent class="p-4">
              <AdTemplatePreviewPanel
                v-if="documentUrl"
                :document-url="documentUrl"
                :html-templates="working.html_templates"
                :variables="working.m_template_attributes"
                :backgrounds="backgrounds"
                :refresh-document-url="refreshDocumentUrl" />
            </CardContent>
          </Card>
        </template>
      </TwoPanePage>

      <ConfirmDialog
        v-model:open="isPublishDialogOpen"
        :title="`Publish v${versionToPublish?.number}?`"
        confirm-label="Publish"
        variant="default"
        @confirm="confirmPublish">
        <template v-if="template.renderer === 'bannerbear'">
          {{ template.name }} switches from Bannerbear to HTML: every new build of the campaigns using it renders this
          version.
        </template>
        <template v-else>
          Every new build renders v{{ versionToPublish?.number }}. Ads already pushed to Facebook keep their images.
        </template>
      </ConfirmDialog>
    </template>
    <div
      v-else-if="!versions.length"
      class="flex flex-col items-start gap-3">
      <p class="text-sm text-muted-foreground">
        This template is still rendered by Bannerbear and has no HTML version yet. Start one from the starter template:
        it keeps the variables of {{ template.name }}, and nothing changes for campaigns until you publish it.
      </p>
      <div class="flex gap-2">
        <Button @click="startHtmlVersion">Start the HTML version</Button>
        <Button
          v-if="canArchive"
          variant="outline"
          :disabled="archiving"
          @click="isArchiveDialogOpen = true">
          Archive
        </Button>
      </div>
    </div>

    <ConfirmDialog
      v-model:open="isArchiveDialogOpen"
      :title="`Archive ${template.name}?`"
      confirm-label="Archive"
      variant="default"
      @confirm="setArchived(true)">
      New creatives can no longer pick {{ template.name }}. The creatives already using it keep it and still build
      with it. You can unarchive it at any time.
    </ConfirmDialog>
  </div>
</template>
